import {
  Container,
  IconButton,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Table,
  TableContainer,
  TableHead,
  Typography,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import NavigationHeader from "../../../components/NavigationHeader";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import PartyActionModal from "./PartyActionModal";
import { Party } from "./types";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import { partyApi } from "./apiService";
import { Loading } from "../../../components/Loading";
import { tableHeaderStyles, tableCellStyles } from "../../../utils/styles";

const Parties = () => {
  const [parties, setParties] = useState<Party[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const { user, loading: authLoading } = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldFetchParties, setShouldFetchParties] = useState(true);

  useEffect(() => {
    if (shouldFetchParties) {
      setIsLoading(true);
      partyApi
        .getParties()
        .then((parties) => {
          setParties(parties);
        })
        .finally(() => {
          setIsLoading(false);
          setShouldFetchParties(false);
        });
    }
  }, [shouldFetchParties]);

  const handleAddParty = () => {
    setOpen(true);
    setSelectedParty(null);
  };

  const handleEditParty = (updatedParty: Party) => {
    setOpen(true);
    setSelectedParty(updatedParty);
  };

  if (authLoading || isLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting Request..." fullScreen={true} />
    ) : (
      <Loading message="Loading Parties..." fullScreen={true} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Parties" },
        ]}
        title="Parties"
        button={{
          label: "Add Party",
          onClick: handleAddParty,
          icon: <AddIcon />,
        }}
      />

      {parties?.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
          No parties found
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="parties table">
            <TableHead>
              <TableRow>
                <TableCell {...tableHeaderStyles}>Party Name</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Name</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Number</TableCell>
                <TableCell {...tableHeaderStyles}>Email</TableCell>
                <TableCell {...tableHeaderStyles}>Address</TableCell>
                <TableCell {...tableHeaderStyles} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parties?.map((party) => (
                <TableRow
                  key={party?.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell {...tableCellStyles}>{party?.partyName}</TableCell>
                  <TableCell {...tableCellStyles}>
                    {party?.primaryContactName || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {party?.primaryContactNumber || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {party?.email || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {party?.address || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles} align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditParty(party)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PartyActionModal
        open={open}
        onClose={() => setOpen(false)}
        party={selectedParty}
        isEdit={!!selectedParty}
        user={user}
        setIsSubmitting={setIsSubmitting}
        setParties={setParties}
        setIsLoading={setIsLoading}
        setShouldFetchParties={setShouldFetchParties}
      />
    </Container>
  );
};

export default Parties;
